import { FormattedMessage, useIntl } from 'react-intl';
import { getDateFnsLocale } from 'shared/hooks/use-locale/date-fns-locale.utils';
import { useLocale } from 'shared/hooks/use-locale/use-locale.hook';
import { Calendar } from 'components/v2/calendar';
import { useFormMethods } from '@hqo/multi-step-form';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { parseISO, format, isValid, isSameDay, addDays, addMonths, addYears } from 'date-fns';
import {
  getLocalDateString,
  calculateEndTime,
  calculateDuration,
  calculateMaxDuration,
  getDefaultStartTime,
} from './when-step.utils';
import { CreateVisitFormV2Values, RepeatType } from '../../types/form-data.types';
import { DateTimeInputGroup, RepeatSection, VisitDayModeToggle } from './components';
import { useVisitStepFieldsConfig } from '../../hooks/use-ui-metadata-field-config.hook';
import { useBuildingSettings } from 'hooks/use-building-settings.hook';
import { useFlag } from 'hooks/use-flag.hook';
import { useMultiDayVisitsEnabled } from 'hooks/use-multi-day-visits.hook';
import { LDFlags, MAX_MULTI_DAY_VISITS } from 'shared/consts';
import { FurthestFutureInviteUnitEnum } from 'store/building-settings/types';
import { useCreateVisitFormV2Context } from '../../context/create-visit-form-v2.context';
import {
  capVisitDates,
  datesToDateOnlyStrings,
  getEarliestVisitDate,
  parseDateOnlyString,
  sortVisitDates,
  toggleVisitDate,
} from '../../utils/visit-dates.utils';

/**
 * Helper function to format time string (HH:mm) based on user's browser locale
 * @param timeString - Time in 24-hour format (HH:mm)
 * @returns Formatted time string respecting user's locale (12h or 24h)
 */
const formatTimeForDisplay = (timeString: string, locale?: string): string => {
  if (!timeString) {
    return '';
  }

  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const WhenStep = (): JSX.Element => {
  const form = useFormMethods() as unknown as UseFormReturn<CreateVisitFormV2Values>;
  const fieldsConfig = useVisitStepFieldsConfig();
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);
  const { formatMessage } = useIntl();
  const showUiMetadata = useFlag(LDFlags.SHOW_VISIT_FORM_UI_METADATA, false);
  const multiDayEnabled = useMultiDayVisitsEnabled();
  const { isExistingSeries, canModifySeriesDates } = useCreateVisitFormV2Context();
  const { settings: buildingSettings } = useBuildingSettings();
  const buildingDefaultStartTime = showUiMetadata ? buildingSettings?.defaultVisitStartTime ?? null : null;
  const showMultiDayToggle = multiDayEnabled || isExistingSeries;

  const maxFutureInviteDate = useMemo(() => {
    if (!showUiMetadata) {
      return undefined;
    }
    const value = buildingSettings?.maxFutureInviteValue;
    const unit = buildingSettings?.maxFutureInviteUnit;
    if (!value || !unit) {
      return undefined;
    }
    const now = new Date();
    switch (unit) {
      case FurthestFutureInviteUnitEnum.DAY:
        return addDays(now, value);
      case FurthestFutureInviteUnitEnum.MONTH:
        return addMonths(now, value);
      case FurthestFutureInviteUnitEnum.YEAR:
        return addYears(now, value);
      default:
        return undefined;
    }
  }, [showUiMetadata, buildingSettings?.maxFutureInviteValue, buildingSettings?.maxFutureInviteUnit]);

  const today = useMemo(() => new Date(), []);
  const todayString = useMemo(() => getLocalDateString(today), [today]);

  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = form;

  const visitStartDate = useWatch({ control, name: 'visitStartDate' });
  const isMultiDay = useWatch({ control, name: 'isMultiDay' }) === true;
  const watchedVisitDates = useWatch({ control, name: 'visitDates' });
  const visitDates = useMemo(() => watchedVisitDates ?? [], [watchedVisitDates]);
  const useDuration = useWatch({ control, name: 'useDuration' });
  const startTime = useWatch({ control, name: 'startTime' });
  const duration = useWatch({ control, name: 'duration' });
  const endTimeValue = useWatch({ control, name: 'endTime' });
  const timeMode = useDuration ? 'duration' : 'endTime';

  // Track previous date to detect date changes
  const previousVisitStartDateRef = useRef<Date | null>(null);
  // Track the last time we programmatically set - used to detect user modifications
  const lastProgrammaticTimeRef = useRef<string | null>(null);

  // When duration is hidden, ensure useDuration is false so endTime validation runs
  useEffect(() => {
    if (fieldsConfig.visitDuration.hidden) {
      setValue('useDuration', false, { shouldValidate: false });
      setValue('duration', undefined, { shouldValidate: false });
    }
  }, [fieldsConfig.visitDuration.hidden, setValue]);

  // Auto-sync visitEndDate to equal visitStartDate whenever visitStartDate changes
  useEffect(() => {
    if (visitStartDate) {
      setValue('visitEndDate', visitStartDate, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitStartDate, setValue]);

  // Default start time based on selected date
  // Only if they haven't manually modified the time field
  useEffect(() => {
    if (!visitStartDate) {
      previousVisitStartDateRef.current = null;
      return;
    }

    const previousDate = previousVisitStartDateRef.current;
    previousVisitStartDateRef.current = visitStartDate;

    // Skip if date hasn't actually changed
    if (previousDate && isSameDay(previousDate, visitStartDate)) {
      return;
    }

    // Skip on initial render (let defaultStartTime from parent work)
    if (!previousDate) {
      // Store the initial time so we can detect user modifications later
      lastProgrammaticTimeRef.current = startTime;
      return;
    }

    // Skip if user has manually modified start time
    // We detect this by checking if current time differs from the last programmatic time
    const userModifiedTime = lastProgrammaticTimeRef.current !== null && startTime !== lastProgrammaticTimeRef.current;
    if (userModifiedTime) {
      return;
    }

    // Get default time for the selected date (null if no change needed)
    const defaultTime = getDefaultStartTime(visitStartDate, buildingDefaultStartTime);
    if (defaultTime) {
      setValue('startTime', defaultTime, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
      // Track what we set so we can detect future user modifications
      lastProgrammaticTimeRef.current = defaultTime;
    }
  }, [visitStartDate, startTime, setValue, buildingDefaultStartTime]);

  // Calculate end time when in duration mode and adjust duration if it exceeds max
  useEffect(() => {
    if (timeMode === 'duration' && startTime && duration) {
      // Only run if we have a valid complete time in HH:mm format
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      const isValidFormat = timeRegex.test(startTime);

      if (!isValidFormat) {
        return; // Skip if time is incomplete or invalid
      }

      const maxDuration = calculateMaxDuration(startTime);

      // If current duration exceeds max, adjust it to max
      if (duration > maxDuration) {
        setValue('duration', maxDuration, { shouldValidate: false });
        return;
      }
      const calculatedEndTime = calculateEndTime(startTime, duration);
      if (calculatedEndTime) {
        setValue('endTime', calculatedEndTime, { shouldValidate: false });
      }
    }
  }, [timeMode, startTime, duration, setValue]);

  // Handle mode switching - calculate duration from existing times when switching to duration mode
  const handleModeChange = useCallback(
    (newMode: 'duration' | 'endTime', onChange: (value: boolean) => void): void => {
      // FIRST: Update the mode flag so validation sees the correct state
      onChange(newMode === 'duration');

      // THEN: Update field values based on new mode
      if (newMode === 'duration' && timeMode === 'endTime') {
        // Clear endTime first
        setValue('endTime', null, { shouldValidate: false });

        // Only calculate duration if we have both start time and end time
        if (startTime && endTimeValue) {
          const calculatedDuration = calculateDuration(startTime, endTimeValue || '');
          if (calculatedDuration && calculatedDuration > 0) {
            const maxDuration = calculateMaxDuration(startTime);
            // Cap duration at max if it exceeds
            const adjustedDuration = Math.min(calculatedDuration, maxDuration);
            // Set duration without immediate validation
            setValue('duration', adjustedDuration, { shouldValidate: false });
          } else {
            // No valid duration calculated, set default duration of 1 hour
            setValue('duration', 1, { shouldValidate: false });
          }
        } else {
          // If no endTime to calculate from, set default duration of 1 hour
          setValue('duration', 1, { shouldValidate: false });
        }
      } else if (newMode === 'endTime') {
        // Clear duration first
        setValue('duration', undefined, { shouldValidate: false });

        // Calculate endTime from duration when switching to endTime mode
        if (startTime && duration && duration > 0) {
          const calculatedEndTime = calculateEndTime(startTime, duration);
          if (calculatedEndTime) {
            // Set endTime without immediate validation
            setValue('endTime', calculatedEndTime, { shouldValidate: false });
          } else {
            // No valid endTime calculated, set default endTime (1 hour from start)
            const defaultEndTime = calculateEndTime(startTime, 1);
            setValue('endTime', defaultEndTime, { shouldValidate: false });
          }
        } else if (startTime) {
          // If no duration to calculate from but we have startTime, set default endTime (1 hour from start)
          const defaultEndTime = calculateEndTime(startTime, 1);
          setValue('endTime', defaultEndTime, { shouldValidate: false });
        } else {
          // If no startTime, can't calculate endTime - leave it null and let validation catch it
          setValue('endTime', null, { shouldValidate: false });
        }
      }

      // FINALLY: Trigger validation after all updates complete
      // setTimeout ensures react-hook-form has processed the mode flag update
      setTimeout(() => {
        trigger(['endTime', 'duration']);
      }, 0);
    },
    [timeMode, startTime, endTimeValue, duration, setValue, trigger],
  );

  // Convert Date to string for calendar/input components
  const getDateString = useCallback(
    (date: Date | null | undefined): string => {
      if (!date || !isValid(date)) {
        return todayString;
      }
      return format(date, 'yyyy-MM-dd');
    },
    [todayString],
  );

  // Convert string to Date
  const parseDateString = useCallback(
    (dateString: string | null | undefined): Date => {
      if (!dateString) {
        return today;
      }
      const parsed = parseISO(dateString);

      return parsed && isValid(parsed) ? parsed : today;
    },
    [today],
  );

  const syncPrimaryDate = useCallback(
    (date: Date): void => {
      setValue('visitStartDate', date, { shouldValidate: false });
      setValue('visitEndDate', date, { shouldValidate: false });
    },
    [setValue],
  );

  const applyVisitDates = useCallback(
    (nextDates: Date[]): void => {
      const sorted = capVisitDates(nextDates);
      setValue('visitDates', sorted, { shouldValidate: true });
      const earliest = getEarliestVisitDate(sorted);
      if (earliest) {
        syncPrimaryDate(earliest);
      }
    },
    [setValue, syncPrimaryDate],
  );

  const handleMultiDayChange = useCallback(
    (nextIsMultiDay: boolean): void => {
      if (!canModifySeriesDates) {
        return;
      }
      setValue('isMultiDay', nextIsMultiDay, { shouldValidate: true });
      if (nextIsMultiDay) {
        let seed = visitDates;
        if (seed.length === 0 && visitStartDate) {
          seed = [visitStartDate];
        }
        applyVisitDates(seed);
        setValue('repeatType', RepeatType.NONE, { shouldValidate: false });
        setValue('repeatEndDate', null, { shouldValidate: false });
        setValue('recurrenceDays', undefined, { shouldValidate: false });
      } else {
        const earliest = getEarliestVisitDate(visitDates) ?? visitStartDate;
        setValue('visitDates', earliest ? [earliest] : [], { shouldValidate: false });
        if (earliest) {
          syncPrimaryDate(earliest);
        }
      }
    },
    [applyVisitDates, canModifySeriesDates, setValue, syncPrimaryDate, visitDates, visitStartDate],
  );

  const handleCalendarSelect = useCallback(
    (value: string | string[] | { from: string; to: string | null } | null): void => {
      if (!value || (typeof value === 'object' && !Array.isArray(value))) {
        return;
      }

      if (isMultiDay) {
        if (!canModifySeriesDates) {
          return;
        }
        if (typeof value === 'string') {
          const parsed = parseDateOnlyString(value);
          if (!parsed) {
            return;
          }
          applyVisitDates(toggleVisitDate(visitDates, parsed));
          return;
        }
        const parsedDates = value
          .map((dateString) => parseDateOnlyString(dateString))
          .filter((date): date is Date => date != null);
        applyVisitDates(parsedDates.length > 0 ? parsedDates : visitDates);
        return;
      }

      if (typeof value === 'string') {
        const selectedDate = parseDateString(value);
        syncPrimaryDate(selectedDate);
        setValue('visitDates', [selectedDate], { shouldValidate: false });
      }
    },
    [applyVisitDates, canModifySeriesDates, isMultiDay, parseDateString, setValue, syncPrimaryDate, visitDates],
  );

  const getCalendarSelected = useCallback(() => {
    if (isMultiDay) {
      return datesToDateOnlyStrings(visitDates);
    }
    return visitStartDate ? getDateString(visitStartDate) : null;
  }, [getDateString, isMultiDay, visitDates, visitStartDate]);

  // Get end time display for duration mode
  const endTimeDisplay = useMemo(() => {
    if (timeMode === 'duration' && endTimeValue) {
      return formatTimeForDisplay(endTimeValue, locale);
    }
    return '';
  }, [timeMode, endTimeValue, locale]);

  const handleDurationChange = useCallback((value: string, onChange: (num: number) => void): void => {
    onChange(Number(value));
  }, []);

  const selectedDateLabels = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
    return sortVisitDates(visitDates)
      .filter((date) => isValid(date))
      .map((date) => formatter.format(date));
  }, [locale, visitDates]);

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4 sm:p-6" data-cy="when-step">
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-semibold">
            <FormattedMessage id="createVisitFormV2.whenStep.title" defaultMessage="When is your visit?" />
          </h3>
        </div>
        <div className="space-y-4 sm:space-y-5">
          {showMultiDayToggle && (
            <VisitDayModeToggle
              isMultiDay={isMultiDay}
              disabled={!canModifySeriesDates}
              onChange={handleMultiDayChange}
            />
          )}
          {isMultiDay && (
            <p className="text-sm text-muted-foreground" data-cy="multi-day-help">
              {canModifySeriesDates
                ? formatMessage({
                    id: 'createVisitFormV2.whenStep.multiDayHelp',
                    defaultMessage:
                      'Select each day this visit should occur. The same start and end time apply to every day.',
                  })
                : formatMessage({
                    id: 'createVisitFormV2.whenStep.datesLockedHelp',
                    defaultMessage: 'These visit days cannot be added or removed.',
                  })}
            </p>
          )}
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6 items-start">
            <div className="w-full md:flex-shrink-0 md:w-auto">
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 border border-gray-200 w-full md:w-fit overflow-x-auto">
                <Calendar
                  mode={isMultiDay ? 'multiple' : 'single'}
                  selected={getCalendarSelected()}
                  onSelect={handleCalendarSelect}
                  className="w-full"
                  disablePast={!isExistingSeries}
                  disableAfter={maxFutureInviteDate}
                  maxSelected={isMultiDay ? MAX_MULTI_DAY_VISITS : undefined}
                  locale={dateFnsLocale}
                  todayLabel={formatMessage({
                    id: 'createVisitFormV2.whenStep.calendarToday',
                    defaultMessage: 'Today',
                  })}
                  selectedLabel={formatMessage({
                    id: 'createVisitFormV2.whenStep.calendarSelected',
                    defaultMessage: 'Selected',
                  })}
                />
              </div>
              {isMultiDay && visitDates.length >= MAX_MULTI_DAY_VISITS && canModifySeriesDates && (
                <p className="text-sm text-muted-foreground mt-1">
                  {formatMessage(
                    {
                      id: 'createVisitFormV2.whenStep.maxDatesReached',
                      defaultMessage: 'You can select up to {max} days.',
                    },
                    { max: MAX_MULTI_DAY_VISITS },
                  )}
                </p>
              )}
              {errors.visitDates && (
                <p className="text-sm text-destructive mt-1">
                  {formatMessage({
                    id: errors.visitDates.message || 'errors.visitDatesRequired',
                    defaultMessage: 'Select at least one visit day',
                  })}
                </p>
              )}
            </div>
            <div className="w-full md:flex-1">
              <DateTimeInputGroup
                control={control}
                errors={errors}
                timeMode={timeMode}
                startTime={startTime}
                endTimeDisplay={endTimeDisplay}
                onTimeModeChange={handleModeChange}
                onDurationChange={handleDurationChange}
                setValue={setValue}
                durationHidden={fieldsConfig.visitDuration.hidden}
                maxStartDate={maxFutureInviteDate}
              />
              {isMultiDay && selectedDateLabels.length > 0 && (
                <div className="mt-2" data-cy="selected-visit-dates">
                  <p className="text-sm text-muted-foreground">
                    {formatMessage({
                      id: 'createVisitFormV2.whenStep.selectedDates',
                      defaultMessage: 'Selected dates',
                    })}
                  </p>
                  <ul className="mt-1 space-y-1">
                    {selectedDateLabels.map((label) => (
                      <li key={label} className="text-sm text-muted-foreground">
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {fieldsConfig.recurrenceEnabled && !isMultiDay && (
                <RepeatSection
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  visitDate={visitStartDate}
                  maxFutureInviteDate={maxFutureInviteDate}
                  triggerValidation={trigger}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
